import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { addLeadComment } from '../../Action/api';

const formatTimestamp = value => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getInitial = name => (name || '?').toString().trim().charAt(0).toUpperCase() || '?';

const currentUserName = () => (localStorage.getItem('userName') || '').trim();

const FollowUpCommentsDialog = ({ open, lead, onClose, onCommentAdded }) => {
  const [newComment, setNewComment] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const scrollRef = useRef(null);

  const comments = lead?.comments || [];
  const me = currentUserName();

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    if (open) {
      setNewComment('');
      setSendError('');
      // Wait for the dialog content to render before jumping to the latest comment.
      requestAnimationFrame(scrollToBottom);
    }
  }, [open, lead?.leadId]);

  useEffect(() => {
    if (open) {
      scrollToBottom();
    }
  }, [open, comments.length]);

  if (!lead) return null;

  const handleSend = async () => {
    const trimmed = newComment.trim();
    if (!trimmed) return;

    setSending(true);
    setSendError('');
    try {
      await addLeadComment(lead, trimmed);
      setNewComment('');
      onCommentAdded?.();
    } catch (err) {
      setSendError(err?.message || 'Failed to add comment.');
    }
    setSending(false);
  };

  const handleKeyDown = event => {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      handleSend();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', maxHeight: '92vh', height: { sm: '88vh' } } }}
    >
      <DialogTitle sx={{ pb: 1.25, display: 'flex', alignItems: 'center', gap: 1.25, borderBottom: '1px solid #e2e8f0' }}>
        <Avatar sx={{ bgcolor: '#eef2ff', color: '#4B5AB6' }}>
          <ChatBubbleOutlineIcon />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '1.1rem' }}>
            Follow-up Comments
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            {lead.fullName || 'Lead'}{lead.companyName ? ` · ${lead.companyName}` : ''}
          </Typography>
        </Box>
        <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>
          {comments.length} comment{comments.length === 1 ? '' : 's'}
        </Typography>
      </DialogTitle>
      <DialogContent
        ref={scrollRef}
        sx={{
          p: { xs: 1.5, sm: 2.5 },
          bgcolor: '#f8fafc',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.75,
          flex: 1,
          overflowY: 'auto',
        }}
      >
        {comments.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6, color: '#94a3b8', m: 'auto' }}>
            <ChatBubbleOutlineIcon sx={{ fontSize: '2.5rem', mb: 1 }} />
            <Typography variant="body2">No follow-up comments yet. Start the conversation below.</Typography>
          </Box>
        ) : (
          comments.map((item, index) => {
            const mine = me && item.commentBy && item.commentBy.toLowerCase() === me.toLowerCase();
            return (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'flex-start',
                  flexDirection: mine ? 'row-reverse' : 'row',
                }}
              >
                <Avatar sx={{ width: 34, height: 34, fontSize: '0.85rem', bgcolor: mine ? '#16A34A' : '#4B5AB6' }}>
                  {getInitial(item.commentBy)}
                </Avatar>
                <Box sx={{ maxWidth: '75%' }}>
                  <Box
                    sx={{
                      bgcolor: mine ? '#e0f2e9' : '#fff',
                      border: '1px solid',
                      borderColor: mine ? '#bbe5cc' : '#e2e8f0',
                      borderRadius: 2,
                      px: 1.75,
                      py: 1.1,
                      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.05)',
                    }}
                  >
                    <Typography variant="body2" sx={{ color: '#1e293b', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                      {item.comment}
                    </Typography>
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#94a3b8',
                      mx: 0.5,
                      mt: 0.4,
                      display: 'block',
                      textAlign: mine ? 'right' : 'left',
                    }}
                  >
                    {item.commentBy || 'Unknown'} · {formatTimestamp(item.commentAt)}
                  </Typography>
                </Box>
              </Box>
            );
          })
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: 'column', alignItems: 'stretch', gap: 1, bgcolor: '#fff' }}>
        {sendError && <Alert severity="error" sx={{ width: '100%' }}>{sendError}</Alert>}
        <TextField
          value={newComment}
          onChange={e => setNewComment(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Add a detailed follow-up note (call outcome, next steps, objections, etc.)..."
          fullWidth
          multiline
          minRows={4}
          maxRows={10}
          disabled={sending}
          sx={{ bgcolor: '#f8fafc', borderRadius: 1 }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
            Ctrl/Cmd + Enter to send
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>
              Close
            </Button>
            <Button
              onClick={handleSend}
              disabled={sending || !newComment.trim()}
              variant="contained"
              startIcon={<SendIcon fontSize="small" />}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 2,
                bgcolor: '#4B5AB6',
                '&:hover': { bgcolor: '#3948a0' },
              }}
            >
              {sending ? 'Sending...' : 'Add Comment'}
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default FollowUpCommentsDialog;
